import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { WorkflowBuilderStoreService } from '../../services/workflow-builder-store.service';
import {
  IAuthorisation,
  IStateMeta,
} from '../../models/workflow-builder.model';
import { EVENTS } from '../../core/catalogs';

const WHO_AUTH: Record<string, IAuthorisation> = {
  'Citizen or Agent': {
    systemAuthorised: false,
    authorisedRoles: ['ROLE_CITIZEN', 'ROLE_AGENT'],
    authorisedUsers: null,
  },
  Citizen: {
    systemAuthorised: false,
    authorisedRoles: ['ROLE_CITIZEN'],
    authorisedUsers: null,
  },
  Agent: {
    systemAuthorised: false,
    authorisedRoles: ['ROLE_AGENT'],
    authorisedUsers: null,
  },
  Officer: {
    systemAuthorised: false,
    authorisedRoles: ['ROLE_OFFICER'],
    authorisedUsers: null,
  },
  'External System using VPN': {
    systemAuthorised: false,
    authorisedRoles: ['ROLE_EXTERNAL_SYSTEM_*'],
    authorisedUsers: null,
  },
  'External System using Public': {
    systemAuthorised: false,
    authorisedRoles: ['ROLE_PUBLIC_EXTERNAL_SYSTEM_*'],
    authorisedUsers: null,
  },
  System: {
    systemAuthorised: true,
    authorisedRoles: null,
    authorisedUsers: null,
  },
};

/** Fallback "who" chip when a copied/parsed authorisation matches no preset. */
const DEFAULT_WHO = 'Citizen or Agent';

/** Inline composer to add a new event (transition) from a state. */
@Component({
  selector: 'irembogov-iwb-add-event',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="composer addinline">
      <button
        *ngIf="clipboardEvent && !lockEvent && !paste"
        class="btn pri iwb-paste-btn"
        style="width:100%;margin-bottom:10px"
        (click)="enterPasteMode()">
        <i class="fa-solid fa-paste"></i> Duplicate event "{{ clipboardEvent }}"
        <small style="opacity:.85">(⌘V)</small>
      </button>
      <div
        *ngIf="paste"
        class="iwb-paste-banner"
        style="
          margin-bottom:10px;padding:8px 10px;border-radius:8px;
          background:#eef2ff;border:1px solid #c7d2fe;color:#3730a3;font-size:12px">
        <i class="fa-solid fa-paste"></i> Duplicating event onto
        <strong>{{ fromState }}</strong> — edit anything below, then Create.
        <div style="margin-top:4px;opacity:.85">
          Copies {{ actionCount }} action{{ actionCount === 1 ? '' : 's'
          }}<span *ngIf="guarded">&nbsp;· keeps its condition (2 outcomes)</span
          >.
        </div>
      </div>
      <div class="q">
        1 · What happens? <small>the event that moves the application</small>
      </div>
      <input *ngIf="lockEvent" class="inp" [value]="lockEvent" disabled />
      <select *ngIf="!lockEvent" class="selx" [(ngModel)]="event">
        <option *ngFor="let e of events" [value]="e">{{ e }}</option>
      </select>

      <div class="q">2 · Who triggers it?</div>
      <div class="chips">
        <span
          class="chip"
          *ngFor="let w of whoOptions"
          [class.on]="who === w"
          (click)="pickWho(w)"
          >{{ w }}</span
        >
      </div>
      <div class="authnote">{{ authNote }}</div>

      <div class="q">
        3 · It moves the application to:<small *ngIf="paste && hasTwo">
          outcome 1 — condition false</small
        >
      </div>
      <div
        class="iwb-destination-mode"
        role="group"
        aria-label="Destination type">
        <button
          type="button"
          class="chip"
          [class.on]="destinationMode === 'existing'"
          (click)="setDestinationMode('existing')">
          Existing state
        </button>
        <button
          type="button"
          class="chip"
          [class.on]="destinationMode === 'new'"
          (click)="setDestinationMode('new')">
          New state
        </button>
      </div>
      <div class="authnote">
        <ng-container *ngIf="destinationMode === 'existing'"
          >Pick an application state that already exists in this
          workflow.</ng-container
        >
        <ng-container *ngIf="destinationMode === 'new'"
          >Pick an application state from the system catalog to bring into this
          workflow.</ng-container
        >
      </div>
      <ng-select
        *ngIf="destinationMode === 'existing'"
        class="iwb-dest"
        [items]="existingOptions"
        bindValue="code"
        [(ngModel)]="dest"
        [searchable]="true"
        [clearable]="false"
        [searchFn]="searchState"
        placeholder="Search by state code or name…"
        dropdownPosition="auto">
        <ng-template ng-label-tmp let-item="item">
          <span
            class="iwb-dot"
            [style.background]="item.colour || 'transparent'"></span>
          <span class="iwb-opt-text">
            <span class="iwb-opt-code">{{ item.code }}</span>
            <small
              class="iwb-opt-name"
              *ngIf="item.name && item.name !== item.code">
              — {{ item.name }}</small
            >
          </span>
        </ng-template>
        <ng-template ng-option-tmp let-item="item">
          <span
            class="iwb-dot"
            [style.background]="item.colour || 'transparent'"></span>
          <span class="iwb-opt-text">
            <small
              class="iwb-opt-name"
              *ngIf="item.name && item.name !== item.code"
              >{{ item.name }}</small
            >
            <span class="iwb-opt-code">{{ item.code }}</span>
          </span>
        </ng-template>
      </ng-select>
      <ng-select
        *ngIf="destinationMode === 'new'"
        class="iwb-dest"
        [items]="newOptions"
        bindValue="code"
        [(ngModel)]="newCode"
        [searchable]="true"
        [clearable]="false"
        [searchFn]="searchState"
        placeholder="Search a system state to add…"
        notFoundText="No system states available"
        dropdownPosition="auto">
        <ng-template ng-label-tmp let-item="item">
          <span
            class="iwb-dot"
            [style.background]="item.colour || 'transparent'"></span>
          <span class="iwb-opt-text">
            <span class="iwb-opt-code">{{ item.code }}</span>
            <small
              class="iwb-opt-name"
              *ngIf="item.name && item.name !== item.code">
              — {{ item.name }}</small
            >
          </span>
        </ng-template>
        <ng-template ng-option-tmp let-item="item">
          <span
            class="iwb-dot"
            [style.background]="item.colour || 'transparent'"></span>
          <span class="iwb-opt-text">
            <small
              class="iwb-opt-name"
              *ngIf="item.name && item.name !== item.code"
              >{{ item.name }}</small
            >
            <span class="iwb-opt-code">{{ item.code }}</span>
          </span>
        </ng-template>
      </ng-select>

      <ng-container *ngIf="paste && hasTwo">
        <div class="q">…and outcome 2 <small>condition true</small></div>
        <div
          class="iwb-destination-mode"
          role="group"
          aria-label="Destination type (outcome 2)">
          <button
            type="button"
            class="chip"
            [class.on]="destinationMode2 === 'existing'"
            (click)="setDestinationMode2('existing')">
            Existing state
          </button>
          <button
            type="button"
            class="chip"
            [class.on]="destinationMode2 === 'new'"
            (click)="setDestinationMode2('new')">
            New state
          </button>
        </div>
        <ng-select
          *ngIf="destinationMode2 === 'existing'"
          class="iwb-dest"
          [items]="existingOptions"
          bindValue="code"
          [(ngModel)]="dest2"
          [searchable]="true"
          [clearable]="false"
          [searchFn]="searchState"
          placeholder="Search by state code or name…"
          dropdownPosition="auto">
          <ng-template ng-label-tmp let-item="item">
            <span
              class="iwb-dot"
              [style.background]="item.colour || 'transparent'"></span>
            <span class="iwb-opt-text">
              <span class="iwb-opt-code">{{ item.code }}</span>
              <small
                class="iwb-opt-name"
                *ngIf="item.name && item.name !== item.code">
                — {{ item.name }}</small
              >
            </span>
          </ng-template>
          <ng-template ng-option-tmp let-item="item">
            <span
              class="iwb-dot"
              [style.background]="item.colour || 'transparent'"></span>
            <span class="iwb-opt-text">
              <small
                class="iwb-opt-name"
                *ngIf="item.name && item.name !== item.code"
                >{{ item.name }}</small
              >
              <span class="iwb-opt-code">{{ item.code }}</span>
            </span>
          </ng-template>
        </ng-select>
        <ng-select
          *ngIf="destinationMode2 === 'new'"
          class="iwb-dest"
          [items]="newOptions"
          bindValue="code"
          [(ngModel)]="newCode2"
          [searchable]="true"
          [clearable]="false"
          [searchFn]="searchState"
          placeholder="Search a system state to add…"
          notFoundText="No system states available"
          dropdownPosition="auto">
          <ng-template ng-label-tmp let-item="item">
            <span
              class="iwb-dot"
              [style.background]="item.colour || 'transparent'"></span>
            <span class="iwb-opt-text">
              <span class="iwb-opt-code">{{ item.code }}</span>
              <small
                class="iwb-opt-name"
                *ngIf="item.name && item.name !== item.code">
                — {{ item.name }}</small
              >
            </span>
          </ng-template>
          <ng-template ng-option-tmp let-item="item">
            <span
              class="iwb-dot"
              [style.background]="item.colour || 'transparent'"></span>
            <span class="iwb-opt-text">
              <small
                class="iwb-opt-name"
                *ngIf="item.name && item.name !== item.code"
                >{{ item.name }}</small
              >
              <span class="iwb-opt-code">{{ item.code }}</span>
            </span>
          </ng-template>
        </ng-select>
      </ng-container>

      <div
        class="aerr"
        *ngIf="error"
        style="color:#b42318;font-size:12px;margin:6px 0">
        {{ error }}
      </div>
      <div class="go">
        <button class="btn pri" (click)="commit()">
          {{ paste ? 'Create copy' : 'Add event' }}
        </button>
        <button class="btn" (click)="cancelled.emit()">Cancel</button>
      </div>
    </div>
  `,
})
export class AddEventComponent {
  @Input() fromState = '';
  @Input() states: IStateMeta[] = [];
  /** Full application-state catalog from the API (preferred over the workflow's own states). */
  @Input() apiStates: IStateMeta[] = [];
  /** When set, the event is fixed (no picker) — used for the mandatory first NEW → APPLY event. */
  @Input() lockEvent = '';
  /**
   * Edit-before-create ("duplicate") mode: prefill the composer from the copied clipboard
   * event so the user can change the event / who / destination(s) before creating, while the
   * copied ACTIONS are carried over. Setting it true seeds the fields once.
   */
  @Input() set paste(on: boolean) {
    this._paste = on;
    if (on) this.applySeed();
  }
  get paste(): boolean {
    return this._paste;
  }
  private _paste = false;
  @Output() done = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  private _existingBase: IStateMeta[] | null = null;
  private _existingOptions: Array<IStateMeta & { code: string }> = [];
  /**
   * "Existing state" picker: the states already present in THIS workflow. Memoised to a STABLE
   * array reference — ng-select rebuilds its option list whenever `[items]` changes identity, and
   * under OnPush a fresh array each change-detection would recreate the options mid-interaction
   * and drop the user's click. Recompute only when the source changes.
   */
  get existingOptions(): Array<IStateMeta & { code: string }> {
    if (this.states !== this._existingBase) {
      this._existingBase = this.states;
      this._existingOptions = [...this.states];
    }
    return this._existingOptions;
  }

  private _newBase: IStateMeta[] | null = null;
  private _newOptions: Array<IStateMeta & { code: string }> = [];
  /**
   * "New state" picker: the full application-engine state catalog fetched from the API. The
   * destination must be a real backend state (a free-typed code would error on save), so this is
   * a catalog picker rather than a free-form form. It is intentionally NOT filtered by the states
   * already in the workflow — the author is bringing a state in from the system, and filtering
   * could leave the list empty. Memoised to a STABLE array reference (recomputed only when the
   * catalog itself changes) for the same reason as {@link existingOptions}.
   */
  get newOptions(): Array<IStateMeta & { code: string }> {
    if (this.apiStates !== this._newBase) {
      this._newBase = this.apiStates;
      this._newOptions = [...this.apiStates];
    }
    return this._newOptions;
  }

  readonly events = EVENTS;
  readonly whoOptions = Object.keys(WHO_AUTH);
  event = EVENTS[0];
  who = DEFAULT_WHO;
  dest = '';
  destinationMode: 'existing' | 'new' = 'existing';
  /** Code of the catalog state chosen in "New state" mode (a real backend state, not free text). */
  newCode = '';
  error = '';

  // ── paste ("duplicate") mode state, seeded from the clipboard transition ──
  /** True when the copied event has a second (conditional) outcome to re-point. */
  hasTwo = false;
  /** Copied action count (both outcomes) — shown so the author knows what carries over. */
  actionCount = 0;
  /** True when the copied event is guarded (2 outcomes). */
  guarded = false;
  /** Destination for the copied second outcome. */
  dest2 = '';
  destinationMode2: 'existing' | 'new' = 'existing';
  /** Code of the catalog state chosen in "New state" mode for the second outcome. */
  newCode2 = '';
  /** The copied event's own authorisation — kept verbatim unless the user changes "who". */
  private seedAuth: IAuthorisation | null = null;
  /** Set once the user actively picks a "who" chip, so we know to override the seed's auth. */
  private whoTouched = false;

  constructor(private store: WorkflowBuilderStoreService) {}

  setDestinationMode(mode: 'existing' | 'new'): void {
    this.destinationMode = mode;
    this.dest = '';
    this.newCode = '';
    this.error = '';
  }

  setDestinationMode2(mode: 'existing' | 'new'): void {
    this.destinationMode2 = mode;
    this.dest2 = '';
    this.newCode2 = '';
    this.error = '';
  }

  /** Record that the user changed "who" so commit overrides the copied auth. */
  pickWho(w: string): void {
    this.who = w;
    this.whoTouched = true;
  }

  /**
   * Prefill the composer from the copied clipboard transition (edit-before-create). The
   * destinations default to where the copy pointed; the author re-points them here so the
   * clone never silently reuses the source's targets.
   */
  private applySeed(): void {
    const seed = this.store.clipboardTransition;
    if (!seed) return;
    this.event = seed.event;
    this.who = this.whoFromAuth(seed.authorisation);
    this.seedAuth = seed.authorisation;
    this.whoTouched = false;
    this.destinationMode = 'existing';
    this.dest = seed.one.stateCode;
    this.hasTwo = !!seed.two;
    this.guarded = !!seed.guard || !!seed.two;
    this.destinationMode2 = 'existing';
    this.dest2 = seed.two?.stateCode ?? '';
    this.actionCount =
      seed.one.actions.length + (seed.two?.actions.length ?? 0);
  }

  /** Best-match a copied authorisation back to a "who" chip (falls back to Citizen or Agent). */
  private whoFromAuth(auth: IAuthorisation | null): string {
    if (!auth) return DEFAULT_WHO;
    const match = Object.keys(WHO_AUTH).find(name => {
      const preset = WHO_AUTH[name];
      return (
        preset.systemAuthorised === auth.systemAuthorised &&
        JSON.stringify(preset.authorisedRoles ?? null) ===
          JSON.stringify(auth.authorisedRoles ?? null)
      );
    });
    return match ?? DEFAULT_WHO;
  }

  /** ng-select filter: match the typed term against either the state code OR its name. */
  searchState = (
    term: string,
    item: IStateMeta & { code: string }
  ): boolean => {
    const t = (term || '').toLowerCase();
    return (
      (item.code || '').toLowerCase().includes(t) ||
      (item.name || '').toLowerCase().includes(t)
    );
  };

  /** Event name on the clipboard (shows the Paste button), or null. */
  get clipboardEvent(): string | null {
    return this.store.clipboardEvent;
  }

  /**
   * Switch the composer into edit-before-create ("duplicate") mode, prefilled from the copied
   * event. Nothing is created yet — the user edits the event / who / destination(s) and then
   * clicks "Create copy". This is the deliberate edit step so a copy never silently points at
   * the source's own targets.
   */
  enterPasteMode(): void {
    if (!this.store.clipboardTransition) return;
    this.paste = true;
  }

  /** ⌘V / Ctrl+V while the composer is open enters duplicate mode (ignored while typing in a field). */
  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if (!(e.metaKey || e.ctrlKey) || (e.key !== 'v' && e.key !== 'V')) return;
    const t = e.target as HTMLElement | null;
    if (
      t &&
      typeof t.closest === 'function' &&
      t.closest('input, textarea, select, [contenteditable]')
    )
      return; // let normal text paste happen
    if (!this.store.clipboardTransition || this.paste || this.lockEvent) return;
    e.preventDefault();
    this.enterPasteMode();
  }

  // The destination starts empty on purpose: the searchable picker shows its placeholder and
  // the user actively chooses (with 400+ backend states, auto-selecting the first is a footgun).

  get authNote(): string {
    const a = WHO_AUTH[this.who];
    return a.systemAuthorised
      ? 'Automatic — the system performs it. No role is added.'
      : 'Not automatic — role required: ' +
          (a.authorisedRoles || []).join(', ');
  }

  /**
   * Resolve the chosen destination code. Both modes now yield a real catalog code: "existing"
   * picks a state already in the workflow, "new" picks a system state not yet in it.
   */
  private resolveDest(
    mode: 'existing' | 'new',
    pick: string,
    newPick: string
  ): string {
    return mode === 'new' ? newPick : pick;
  }

  commit(): void {
    this.error = '';
    const event = (this.lockEvent || this.event)
      .toUpperCase()
      .replace(/\s+/g, '_');
    if (!event) {
      this.error = 'Choose an event.';
      return;
    }
    const one = this.resolveDest(this.destinationMode, this.dest, this.newCode);
    if (!one) {
      this.error =
        this.destinationMode === 'new'
          ? 'Choose a system state to add.'
          : 'Choose a destination state.';
      return;
    }

    if (this.paste) {
      let two: string | null = null;
      if (this.hasTwo) {
        two = this.resolveDest(
          this.destinationMode2,
          this.dest2,
          this.newCode2
        );
        if (!two) {
          this.error =
            this.destinationMode2 === 'new'
              ? 'Choose a system state to add for the second outcome.'
              : 'Choose a destination for the second outcome.';
          return;
        }
      }
      // Keep the copied auth verbatim unless the user actively changed "who".
      const auth = this.whoTouched ? WHO_AUTH[this.who] : this.seedAuth;
      const ok = this.store.pasteEventEdited({
        startState: this.fromState,
        event,
        auth,
        oneStateCode: one,
        twoStateCode: two,
      });
      if (!ok) {
        this.error = `"${this.fromState}" already has a "${event}" event — pick a different event.`;
        return;
      }
      this.done.emit();
      return;
    }

    // addTransition is a no-op when (startState, event) already exists — surface that
    // instead of silently closing, so "add a new state" never appears to do nothing.
    const added = this.store.addTransition({
      startState: this.fromState,
      event,
      toStateCode: one,
      auth: WHO_AUTH[this.who],
    });
    if (!added) {
      this.error = `"${this.fromState}" already has a "${event}" event — pick a different event.`;
      return;
    }
    this.done.emit();
  }
}
